import { FC, PropsWithChildren, ReactNode } from 'react';

import { PLP_BASE_PATH } from '@/plp/helpers/getPlpSlug';
import {
  CommonRangeFilterTypeMap,
  processNumberUnit,
  processPriceUnit,
  processUnitDoNothing,
} from '@/plp/helpers/utils';
import { FilterValues } from '@/types/CataloguePage.types';

import { FilterOption, RangeFilterType, UnitProcessor } from '../PlpFilters/PlpFilters.type';
import { filterRangeFormatting } from '../PlpFilters/plpFiltersMap';
import { Chip } from './PlpFilterChips.type';

type FilterObjectWithType = FilterOption & { type: string };

type RangeFilterValue = {
  min?: string;
  max?: string;
};

type RangeChipLabelSettingsMap = Map<
  string,
  {
    prefix: ReactNode;
    preUnit: ReactNode;
    postUnit: ReactNode;
    processUnit: UnitProcessor;
    minRangeName: FilterValues;
    maxRangeName: FilterValues;
  }
>;

type GenerateHref = (filterString?: string) => GenerateHrefHandler;

type GenerateHrefHandler = (filterSlug: string) => string | undefined;

const PostUnit: FC<PropsWithChildren> = ({ children }) => <span className="normal-case">{children}</span>;

const RangeChipLabelSettings: RangeChipLabelSettingsMap = new Map([
  [
    RangeFilterType.price,
    {
      prefix: null,
      preUnit: '$',
      postUnit: null,
      processUnit: processPriceUnit,
      minRangeName: FilterValues.CashMin,
      maxRangeName: FilterValues.CashMax,
    },
  ],
  [
    RangeFilterType.finance,
    {
      prefix: null,
      preUnit: '$',
      postUnit: <PostUnit>/week</PostUnit>,
      processUnit: processNumberUnit,
      minRangeName: FilterValues.FinanceMin,
      maxRangeName: FilterValues.FinanceMax,
    },
  ],
  [
    RangeFilterType.year,
    {
      prefix: null,
      preUnit: null,
      postUnit: null,
      processUnit: processUnitDoNothing,
      minRangeName: FilterValues.YearMin,
      maxRangeName: FilterValues.YearMax,
    },
  ],
  [
    RangeFilterType.km,
    {
      prefix: null,
      preUnit: null,
      postUnit: <PostUnit>km</PostUnit>,
      processUnit: processNumberUnit,
      minRangeName: FilterValues.KMMin,
      maxRangeName: FilterValues.KMMax,
    },
  ],
  [
    RangeFilterType.fuel,
    {
      prefix: null,
      preUnit: null,
      postUnit: <PostUnit>L/100km</PostUnit>,
      processUnit: processNumberUnit,
      minRangeName: FilterValues.FuelConsumptionMin,
      maxRangeName: FilterValues.FuelConsumptionMax,
    },
  ],
  [
    RangeFilterType.towCap,
    {
      prefix: 'Tow ',
      preUnit: null,
      postUnit: <PostUnit>kg</PostUnit>,
      processUnit: processNumberUnit,
      minRangeName: FilterValues.TowCapBrakedMin,
      maxRangeName: FilterValues.TowCapBrakedMax,
    },
  ],
]);

const rangeFilterValues: FilterValues[] = filterRangeFormatting.flatMap((item) => [item.min, item.max]);

export const generateChips = (filterObjects: FilterOption[], filterString?: string) => {
  const chips: Chip[] = [];

  const filterObjectsWithType: FilterObjectWithType[] = filterObjects.map((filterObject) => {
    if (rangeFilterValues.includes(filterObject.filter)) {
      const rangeFilterWithType = {
        ...filterObject,
        type: CommonRangeFilterTypeMap.get(filterObject.filter) as string,
      };
      return rangeFilterWithType;
    }

    return {
      ...filterObject,
      type: filterObject.filter,
    };
  });

  const groupedChipsWithFilter = filterObjectsWithType.reduce((acc, item) => {
    acc.set(item.type, [...(acc.get(item.type) || []), item]);
    return acc;
  }, new Map<string, FilterObjectWithType[]>());

  chips.push(...generateNormalChip(groupedChipsWithFilter, FilterValues.Keyword, generateHref(filterString)));
  chips.push(...generateNormalChip(groupedChipsWithFilter, FilterValues.State, generateHref(filterString)));
  chips.push(...generateNormalChip(groupedChipsWithFilter, FilterValues.Transmission, generateHref(filterString)));
  chips.push(...generateNormalChip(groupedChipsWithFilter, FilterValues.Make, generateHref(filterString)));
  chips.push(...generateParentChildChip(groupedChipsWithFilter, FilterValues.Model, generateHref(filterString)));
  chips.push(...generateNormalChip(groupedChipsWithFilter, FilterValues.BodyType, generateHref(filterString)));
  chips.push(...generateParentChildChip(groupedChipsWithFilter, FilterValues.BodySize, generateHref(filterString)));
  chips.push(...generateRangeChip(groupedChipsWithFilter, RangeFilterType.price, generateHref(filterString)));
  chips.push(...generateRangeChip(groupedChipsWithFilter, RangeFilterType.finance, generateHref(filterString)));
  chips.push(...generateRangeChip(groupedChipsWithFilter, RangeFilterType.year, generateHref(filterString)));
  chips.push(...generateRangeChip(groupedChipsWithFilter, RangeFilterType.km, generateHref(filterString)));
  chips.push(...generateRangeChip(groupedChipsWithFilter, RangeFilterType.fuel, generateHref(filterString)));
  chips.push(...generateNormalChip(groupedChipsWithFilter, FilterValues.FuelCategory, generateHref(filterString)));
  chips.push(...generateNormalChip(groupedChipsWithFilter, FilterValues.FuelSubType, generateHref(filterString)));
  chips.push(
    ...generateNormalChip(groupedChipsWithFilter, FilterValues.HighlightedFeatures, generateHref(filterString)),
  );
  chips.push(...generateNormalChip(groupedChipsWithFilter, FilterValues.DriveType, generateHref(filterString)));
  chips.push(...generateNormalChip(groupedChipsWithFilter, FilterValues.Seats, generateHref(filterString)));
  chips.push(...generateNormalChip(groupedChipsWithFilter, FilterValues.Doors, generateHref(filterString)));
  chips.push(...generateNormalChip(groupedChipsWithFilter, FilterValues.Colour, generateHref(filterString)));
  chips.push(...generateNormalChip(groupedChipsWithFilter, FilterValues.Ancap, generateHref(filterString)));
  chips.push(...generateNormalChip(groupedChipsWithFilter, FilterValues.Co2emissions, generateHref(filterString)));
  chips.push(...generateRangeChip(groupedChipsWithFilter, RangeFilterType.towCap, generateHref(filterString)));
  return chips;
};

const generateNormalChip = (
  groupedChipsWithFilter: Map<string, FilterObjectWithType[]>,
  filterType: string,
  generateHrefHandler: GenerateHrefHandler,
) => {
  const filterObjectsWithType = groupedChipsWithFilter.get(filterType);
  if (!filterObjectsWithType) {
    return [];
  }
  return filterObjectsWithType.map((filterObject) => {
    return {
      key: filterObject.slug,
      label: filterObject.label?.length ? filterObject.label : filterObject.slug,
      href: generateHrefHandler(filterObject.slug),
    };
  });
};

const generateParentChildChip = (
  groupedChipsWithFilter: Map<string, FilterObjectWithType[]>,
  filterValue: FilterValues,
  generateHrefHandler: GenerateHrefHandler,
) => {
  const filterObjectsWithType = groupedChipsWithFilter.get(filterValue);
  if (!filterObjectsWithType) {
    return [];
  }
  return filterObjectsWithType.map((filterObject) => {
    const isParentFirst = filterValue === FilterValues.Model;
    const label = formatParentChildItems(filterObject.label ?? '', filterObject.slug, isParentFirst);

    return {
      key: filterObject.slug,
      label: label,
      href: generateHrefHandler(filterObject.slug),
    };
  });
};

const generateRangeChip = (
  groupedChipsWithFilter: Map<string, FilterObjectWithType[]>,
  filterType: string,
  generateHrefHandler: GenerateHrefHandler,
): Chip[] => {
  const filterObjectWithType = groupedChipsWithFilter.get(filterType);
  if (!filterObjectWithType) return [];
  const rangeFilterSettings = RangeChipLabelSettings.get(filterType);
  if (!rangeFilterSettings) return [];
  const minFilter = filterObjectWithType.find((item) => item?.filter === rangeFilterSettings.minRangeName);
  const maxFilter = filterObjectWithType.find((item) => item?.filter === rangeFilterSettings.maxRangeName);
  const chipLabel = labelizeRangeChip(
    {
      min: minFilter?.value,
      max: maxFilter?.value,
    },
    rangeFilterSettings.preUnit,
    rangeFilterSettings.postUnit,
    rangeFilterSettings.processUnit,
    rangeFilterSettings.prefix,
  );
  return [
    {
      key: filterObjectWithType[0]?.slug ?? '',
      label: chipLabel,
      href: generateHrefHandler(filterObjectWithType[0]?.slug ?? ''),
    },
  ];
};

const labelizeRangeChip = (
  rangeFilterValues: RangeFilterValue,
  preUnit: ReactNode,
  postUnit: ReactNode,
  processUnit: UnitProcessor,
  prefix: ReactNode,
): ReactNode => {
  const { min, max } = rangeFilterValues;

  if (min && max) {
    const processedMin = processUnit(min);
    const processedMax = processUnit(max);
    return min === max ? (
      <>
        {prefix}
        {preUnit}
        {processedMin}
        {postUnit}
      </>
    ) : (
      <>
        {prefix}
        {preUnit}
        {processedMin} - {preUnit}
        {processedMax}
        {postUnit}
      </>
    );
  }

  if (min) {
    return (
      <>
        {prefix}
        From {preUnit}
        {processUnit(min)}
        {postUnit}
      </>
    );
  }

  if (max) {
    return (
      <>
        {prefix}
        Up to {preUnit}
        {processUnit(max)}
        {postUnit}
      </>
    );
  }

  return '';
};

const generateHref: GenerateHref = (filterString?: string) => (filterSlug: string) => {
  if (!filterString) return `${PLP_BASE_PATH}/${filterSlug}`;
  if (!filterString.includes('_')) {
    return PLP_BASE_PATH;
  }
  const newFilters = filterString.split('_').filter((item) => item !== filterSlug);
  return `${PLP_BASE_PATH}/${newFilters.join('_')}`;
};

export const formatParentChildItems = (label: string, slug: string, isParentFirst: boolean = false) => {
  const formattedLabel = label.toLowerCase().replaceAll(' ', '-');
  const parentLabel = slug.replaceAll(formattedLabel, '').replaceAll('-', '');
  if (!label.length) return parentLabel;
  if (!parentLabel.length) return label;
  return isParentFirst ? `${parentLabel} ${label}` : `${label} ${parentLabel}`;
};
